import React, { useEffect, useState } from 'react';
import {
  Check, Sparkles, Crown, Rocket, ShoppingCart, Landmark, Layers, Building2,
  ArrowRight, Shield, Anchor, FileSignature, Users, Coins,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';

// ---------- Canonical formation tiers (pre-configured structure packages) ----------
// These mirror the options inside the /formation wizard so users who want a
// quick-pick don't have to walk through the 4 steps.

const STRUCTURE_TIERS = [
  {
    id: 'single_llc',
    name: 'Single Entity',
    description: 'One LLC or Corporation — Delaware baseline',
    icon: Building2,
    quoteInputs: {
      complexity: 'single',
      jurisdictions: 'single_state',
      states: ['DE'],
      asset_types: ['operating'],
      executive: false,
      include_registered_agent: true,
    },
    features: [
      '1 prepaid entity credit',
      'Operating agreement & formation docs',
      'Delaware filing + registered agent',
      'All compliance reminders & audit certs',
    ],
    popular: false,
  },
  {
    id: 'multi_entity',
    name: 'Multi-Entity',
    description: '3 related entities for growing founders',
    icon: Layers,
    quoteInputs: {
      complexity: 'multi_entity',
      jurisdictions: 'single_state',
      states: ['DE'],
      asset_types: ['operating'],
      executive: false,
      include_registered_agent: true,
    },
    features: [
      '3 prepaid entity credits',
      'Multi-entity bundle structuring',
      'Delaware filings + registered agents',
      'Use credits whenever you incorporate',
    ],
    popular: true,
  },
  {
    id: 'holding_stack',
    name: 'Holding + Subs',
    description: 'Parent/child liability firewall, multi-state',
    icon: Landmark,
    quoteInputs: {
      complexity: 'holding_plus_subs',
      jurisdictions: 'multi_state',
      states: ['DE', 'WY', 'TX'],
      asset_types: ['operating', 'real_estate', 'ip_portfolio'],
      executive: false,
      include_registered_agent: true,
    },
    features: [
      '4 prepaid entity credits',
      'Holding company architecture',
      'Real-estate + IP protection layers',
      'Multi-state qualification',
    ],
    popular: false,
  },
  {
    id: 'executive',
    name: 'Executive',
    description: 'Unlimited complexity, flat one-time fee',
    icon: Crown,
    quoteInputs: {
      executive: true,
      states: ['DE', 'WY', 'CA'],
      include_registered_agent: true,
    },
    features: [
      '7 prepaid entity credits',
      'Holding + Subs + Trust + IP portfolio',
      'Multi-state qualification included',
      'Priority support & dedicated architect',
    ],
    popular: false,
    badge: 'FLAT $12,500',
  },
];

// ---------- Optional add-ons ----------

const ADD_ONS = [
  {
    id: 'certification',
    name: 'CorpCraft Certification',
    description: 'Official compliance certification badge',
    price: 99,
    priceCrypto: 91,
    icon: Shield,
    features: ['Official CorpCraft certification', 'Verified badge on certificates'],
  },
  {
    id: 'blockchain_anchor',
    name: 'Blockchain Anchor',
    description: 'Anchor certificate to Ethereum',
    price: 29,
    priceCrypto: 27,
    icon: Anchor,
    features: ['Ethereum blockchain anchoring', 'Immutable certificate proof'],
  },
  {
    id: 'email_signing_pack',
    name: 'Email Signing Pack',
    description: '5 formation document email signings',
    price: 49,
    priceCrypto: 45,
    icon: FileSignature,
    features: ['5 document signing links via email', 'Multi-party signature collection'],
  },
  {
    id: 'team_seat',
    name: 'Team Seat',
    description: 'Additional team member',
    price: 25,
    priceCrypto: 23,
    icon: Users,
    billing: '/month',
    features: ['Additional team member access', 'Full feature access'],
  },
];

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { items, addItem, totals } = useCart();
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [quotes, setQuotes] = useState({}); // tier.id → quote
  const [credits, setCredits] = useState(null);
  const [loadingQuotes, setLoadingQuotes] = useState(true);

  useEffect(() => {
    // Load live quotes for each structure tier from the backend calculator
    const loadQuotes = async () => {
      setLoadingQuotes(true);
      try {
        const results = await Promise.all(
          STRUCTURE_TIERS.map((t) =>
            api.post('/formation/quote', t.quoteInputs).then((r) => [t.id, r.data]).catch(() => [t.id, null]),
          ),
        );
        const map = Object.fromEntries(results);
        setQuotes(map);
      } finally {
        setLoadingQuotes(false);
      }
    };
    loadQuotes();

    if (user) {
      api.get('/formation/credits').then((r) => setCredits(r.data)).catch(() => {});
    }
  }, [user]);

  const inCartFormation = (tierId) => items.some(
    (it) => it.service_id === 'formation_package' && it.label?.toLowerCase().includes(tierId === 'executive' ? 'executive' : ''),
  );

  const inCartAddon = (serviceId) => items.some((it) => it.service_id === serviceId);

  const handleAddStructure = (tier) => {
    if (!user) {
      navigate('/login');
      return;
    }
    const quote = quotes[tier.id];
    if (!quote) {
      toast.error('Could not price this package — try again in a moment');
      return;
    }
    addItem('formation_package', 1, {
      priceUsd: quote.total_usd,
      priceUsdc: quote.total_usdc,
      formationInputs: tier.quoteInputs,
      entityCredits: quote.entity_credits_awarded,
      aLaCarteSavings: quote.a_la_carte_savings_usd || 0,
      label: tier.name === 'Executive' ? 'Executive Formation Package' : `${tier.name} Package`,
    });
    toast.success(`${tier.name} added — ${quote.entity_credits_awarded} entity credits on checkout`, {
      action: { label: 'Go to cart', onClick: () => navigate('/cart') },
    });
  };

  const handleConfigureCustom = () => {
    if (!user) return navigate('/login');
    navigate('/formation');
  };

  const handleAddAddon = (addon) => {
    if (!user) return navigate('/login');
    addItem(addon.id);
    toast.success(`${addon.name} added to cart`, {
      action: { label: 'View Cart', onClick: () => navigate('/cart') },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-12 px-4" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 font-['Outfit']">Structure-Based Pricing</h1>
          <p className="text-lg md:text-xl text-slate-300 mb-2">
            One-time platform fee based on complexity. Prepaid entity credits used over time.
          </p>
          <p className="text-slate-400 text-sm max-w-2xl mx-auto">
            Pay once for your structure. Log in and out as you need — use your credits whenever you're ready to incorporate.
            Once credits run out, single-entity filings are pay-as-you-go.
          </p>

          {/* Credits & cart banners */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            {user && credits && (
              <div className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 rounded-full px-4 py-2" data-testid="pricing-credits-banner">
                <Coins className="w-4 h-4" />
                <span className="text-sm font-medium">
                  You have {credits.remaining} entity credit{credits.remaining === 1 ? '' : 's'} remaining
                </span>
              </div>
            )}
            {totals.itemCount > 0 && (
              <div className="inline-flex items-center gap-2 bg-blue-500/15 border border-blue-500/40 text-blue-200 rounded-full px-4 py-2" data-testid="pricing-cart-banner">
                <ShoppingCart className="w-4 h-4" />
                <span className="text-sm">
                  {totals.itemCount} in cart · ${totals.totalUsd.toFixed(2)}
                </span>
                <Button size="sm" onClick={() => navigate('/cart')} className="h-7 bg-blue-500 hover:bg-blue-400 ml-1" data-testid="pricing-go-to-cart-btn">
                  Go to cart
                </Button>
              </div>
            )}
          </div>

          {/* Payment toggle */}
          <div className="inline-flex bg-slate-800 rounded-lg p-1 mt-6">
            <button
              onClick={() => setPaymentMethod('paypal')}
              data-testid="pricing-method-paypal"
              className={`px-6 py-2 rounded-md transition ${paymentMethod === 'paypal' ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              USD (PayPal)
            </button>
            <button
              onClick={() => setPaymentMethod('crypto')}
              data-testid="pricing-method-crypto"
              className={`px-6 py-2 rounded-md transition ${paymentMethod === 'crypto' ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              USDC (Save 8%)
            </button>
          </div>
        </div>

        {/* Structure Tiers */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-center mb-2">Formation Packages</h2>
          <p className="text-center text-slate-400 text-sm mb-8">
            One-time platform fee · Government filing fees pass-through · Credits never expire
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STRUCTURE_TIERS.map((tier) => {
              const Icon = tier.icon;
              const quote = quotes[tier.id];
              const displayPrice = paymentMethod === 'crypto' ? quote?.total_usdc : quote?.total_usd;
              const added = inCartFormation(tier.id);
              const isExec = tier.id === 'executive';

              return (
                <div
                  key={tier.id}
                  data-testid={`tier-card-${tier.id}`}
                  className={`relative bg-slate-800/60 border rounded-xl p-6 transition-all flex flex-col ${
                    added ? 'border-emerald-500 shadow-lg shadow-emerald-500/20' :
                    isExec ? 'border-amber-500/50 shadow-lg shadow-amber-500/20' :
                    tier.popular ? 'border-blue-500/60 shadow-lg shadow-blue-500/20' :
                    'border-slate-700 hover:border-blue-500/50'
                  }`}
                >
                  {tier.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs px-4 py-1 rounded-full font-bold">
                      {tier.badge}
                    </div>
                  )}
                  {tier.popular && !tier.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs px-4 py-1 rounded-full font-bold">
                      MOST POPULAR
                    </div>
                  )}

                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto ${
                    isExec ? 'bg-amber-500/20' : 'bg-blue-500/20'
                  }`}>
                    <Icon className={`w-7 h-7 ${isExec ? 'text-amber-400' : 'text-blue-400'}`} />
                  </div>

                  <h3 className="text-2xl font-bold mb-1 text-center">{tier.name}</h3>
                  <p className="text-slate-400 text-sm mb-5 text-center h-10">{tier.description}</p>

                  <div className="mb-5 text-center">
                    {loadingQuotes ? (
                      <div className="h-14 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : quote ? (
                      <>
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl md:text-5xl font-bold" data-testid={`tier-price-${tier.id}`}>
                            ${(displayPrice || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-slate-500 text-xs mt-1">
                          one-time · {paymentMethod === 'crypto' ? 'USDC' : 'USD'}
                          {paymentMethod === 'crypto' && quote.savings_usd > 0 && (
                            <span className="text-emerald-400 ml-1">save ${quote.savings_usd.toFixed(0)}</span>
                          )}
                        </div>
                        <div className="inline-flex items-center gap-1 mt-3 bg-blue-500/10 border border-blue-500/30 rounded-full px-3 py-1">
                          <Coins className="w-3.5 h-3.5 text-blue-300" />
                          <span className="text-xs text-blue-200 font-medium">
                            {quote.entity_credits_awarded} entity credit{quote.entity_credits_awarded === 1 ? '' : 's'}
                          </span>
                        </div>
                        {quote.a_la_carte_savings_usd > 0 && (
                          <div
                            data-testid={`tier-savings-${tier.id}`}
                            className="inline-flex items-center gap-1 mt-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1"
                          >
                            <Sparkles className="w-3 h-3 text-emerald-300" />
                            <span className="text-xs text-emerald-200 font-medium">
                              Save ${quote.a_la_carte_savings_usd.toLocaleString()} vs. a la carte
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-xs text-slate-500 italic">Price unavailable</div>
                    )}
                  </div>

                  <ul className="space-y-2.5 mb-6 flex-1">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isExec ? 'text-amber-400' : 'text-emerald-400'}`} />
                        <span className="text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {added ? (
                    <Button
                      onClick={() => navigate('/cart')}
                      data-testid={`tier-cart-go-${tier.id}`}
                      className="w-full bg-emerald-500 hover:bg-emerald-400 text-white"
                    >
                      <Check className="w-4 h-4 mr-2" /> In cart
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleAddStructure(tier)}
                      disabled={!quote}
                      data-testid={`tier-add-${tier.id}`}
                      className={`w-full ${
                        isExec ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-white' :
                        tier.popular ? 'gradient-accent hover:opacity-90' :
                        'bg-slate-700 hover:bg-slate-600'
                      }`}
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" /> Add to cart
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Custom configurator CTA */}
          <div className="mt-8 text-center" data-testid="pricing-configure-banner">
            <p className="text-slate-400 text-sm mb-2">Need a different configuration?</p>
            <Button
              onClick={handleConfigureCustom}
              variant="outline"
              className="border-blue-500 text-blue-300 hover:bg-blue-500/10"
              data-testid="pricing-configure-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Build a custom package
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Pay-as-you-go single entity notice */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-16 flex items-start gap-4" data-testid="payg-notice">
          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <Coins className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="font-semibold mb-1">After your credits run out</h3>
            <p className="text-sm text-slate-400">
              Once you've used all your prepaid entity credits, any additional single-entity formation
              is billed at our flat <span className="text-white font-semibold">$2,500 per entity</span>{' '}
              (+ state filing fees). No surprises, no subscription — pay only when you incorporate.
            </p>
          </div>
        </div>

        {/* Add-ons */}
        <div>
          <h2 className="text-2xl font-bold text-center mb-2">Optional Add-Ons</h2>
          <p className="text-center text-slate-400 mb-8">Stack as many as you like — they all check out together.</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ADD_ONS.map((addon) => {
              const Icon = addon.icon;
              const displayPrice = paymentMethod === 'crypto' ? addon.priceCrypto : addon.price;
              const added = inCartAddon(addon.id);
              return (
                <div
                  key={addon.id}
                  data-testid={`addon-card-${addon.id}`}
                  className={`bg-slate-800/50 border rounded-xl p-5 transition-all ${
                    added ? 'border-emerald-500' : 'border-slate-700 hover:border-blue-500'
                  }`}
                >
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <h4 className="font-bold mb-1">{addon.name}</h4>
                  <p className="text-slate-400 text-xs mb-3">{addon.description}</p>

                  <div className="mb-3">
                    <span className="text-2xl font-bold">${displayPrice}</span>
                    <span className="text-slate-400 text-sm ml-1">{paymentMethod === 'crypto' ? 'USDC' : 'USD'}</span>
                    {addon.billing && <span className="text-slate-400 text-sm">{addon.billing}</span>}
                  </div>

                  <ul className="space-y-1 mb-4">
                    {addon.features.slice(0, 2).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-1 text-xs">
                        <Check className="w-3 h-3 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-400">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {added ? (
                    <Button
                      onClick={() => navigate('/cart')}
                      data-testid={`addon-cart-go-${addon.id}`}
                      variant="outline"
                      size="sm"
                      className="w-full text-xs border-emerald-500 text-emerald-400 hover:bg-emerald-500/10"
                    >
                      <Check className="w-3 h-3 mr-1" /> In cart
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleAddAddon(addon)}
                      data-testid={`addon-add-${addon.id}`}
                      variant="outline"
                      size="sm"
                      className="w-full text-xs border-slate-700 hover:border-blue-500"
                    >
                      <ShoppingCart className="w-3 h-3 mr-1" /> Add to cart
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Info */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mt-12">
          <h3 className="text-xl font-bold mb-4">How the model works</h3>
          <div className="grid md:grid-cols-3 gap-6 text-sm">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded bg-blue-500/20 text-blue-300 flex items-center justify-center text-xs font-bold">1</div>
                <h4 className="font-semibold text-white">Pick your structure tier</h4>
              </div>
              <p className="text-slate-400">Pay once based on complexity. No monthly billing — ever.</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded bg-blue-500/20 text-blue-300 flex items-center justify-center text-xs font-bold">2</div>
                <h4 className="font-semibold text-white">Get prepaid entity credits</h4>
              </div>
              <p className="text-slate-400">Each tier awards credits you can redeem whenever you're ready to incorporate.</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded bg-blue-500/20 text-blue-300 flex items-center justify-center text-xs font-bold">3</div>
                <h4 className="font-semibold text-white">Create entities anytime</h4>
              </div>
              <p className="text-slate-400">Log in and out, create entities over months or years. Credits never expire.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
